/**
 * Shared helpers for the time-entry project pickers, used by the web frontend
 * and the mobile app so the two stay in lockstep. The `my-projects` endpoint
 * returns each project with `last_used` (the requesting user's most recent time
 * entry against it), letting the pickers surface most-recently-used first and
 * "narrow" the default view to the top N (MT-1565), and drive the running-entry
 * project switch (MT-1564).
 */

import { TimerStopRefusal } from '../enums.js';

/** A project the current user may log time against, as returned by `/time-entries/my-projects`. */
export interface TimeProjectOption {
  id: string;
  name: string;
  group_name: string;
  department_name: string;
  /** ISO timestamp of the user's most recent entry on this project, or null/absent if never used. */
  last_used?: string | null;
}

/** Human label for a project option: `Department / Project`. */
export function projectLabel(p: Pick<TimeProjectOption, 'name' | 'department_name'>): string {
  return `${p.department_name} / ${p.name}`;
}

/**
 * Order projects most-recently-used first. Never-used projects keep their
 * incoming (alphabetical) order after the used ones — `Array.prototype.sort` is
 * stable, so equal recency (including the 0 assigned to unused projects)
 * preserves the original relative order.
 */
export function sortProjectsByRecency<T extends { last_used?: string | null }>(projects: T[]): T[] {
  const ts = (v: string | null | undefined) => (v ? new Date(v).getTime() : 0);
  return [...projects].sort((a, b) => ts(b.last_used) - ts(a.last_used));
}

/** Fallback shown until the configured `TIME_DEFAULT_PROJECT_COUNT` loads. */
export const DEFAULT_PROJECT_COUNT = 5;

/**
 * Start time for the entry that replaces a just-stopped one when switching
 * project. It is **one second past** the stopped entry's `end_time`, not the
 * stopped end itself: the stop response's `end_time` is millisecond-truncated
 * ISO while Postgres stores microseconds, so starting exactly at it lands a hair
 * *before* the stored end and the server's overlap check (`end_time > new_start`)
 * would reject the new open timer. One second clears that boundary and is
 * invisible at the minute granularity used for reporting/payroll, keeping the
 * handoff continuous in practice.
 *
 * Never earlier than `now`: when the backend clamps an overlong timer to a
 * date-limited project's end boundary, the returned `end_time` can be hours or
 * days in the past, and starting the new entry there would backdate it (banking
 * unworked time, or failing creation against the new project's date range). So
 * the start is `max(end + 1s, now)`. Falls back to `now` when the end is unknown.
 */
export function switchHandoffStart(
  stoppedEndTime: string | null | undefined,
  now: () => number = Date.now,
): string {
  const nowMs = now();
  if (!stoppedEndTime) return new Date(nowMs).toISOString();
  return new Date(Math.max(new Date(stoppedEndTime).getTime() + 1000, nowMs)).toISOString();
}

/**
 * A stop refused because the timer is no longer running — closed in another
 * tab or device (409 `already_stopped`, with the entry) or deleted (404). Read
 * from an API error's `status` and body; null for any other error. Both apps
 * use it so a stale running timer is cleared instead of shown as a failure.
 */
export interface StoppedElsewhere {
  /** When it was stopped (ISO), when the server said so. */
  endTime: string | null;
  /** The entry no longer exists. */
  deleted: boolean;
}

export function stoppedElsewhereFrom(status: number, data: Record<string, unknown> | undefined): StoppedElsewhere | null {
  if (status === 404) return { endTime: null, deleted: true };
  if (status !== 409 || data?.code !== TimerStopRefusal.AlreadyStopped) return null;
  const entry = data.entry as { end_time?: string | null } | undefined;
  return { endTime: entry?.end_time ?? null, deleted: false };
}

/** What to tell the user when their timer turns out to be stopped already. */
export function stoppedElsewhereMessage(s: StoppedElsewhere, locale?: string): string {
  if (s.deleted) return 'This timer no longer exists — it was removed elsewhere.';
  const at = s.endTime
    ? ` at ${new Date(s.endTime).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })}`
    : '';
  return `This timer was already stopped${at}, probably from another tab or device. Your time is saved.`;
}
